/**
 * Single source of truth for POCK quantity <-> percentage <-> money math.
 *
 * A POCK line stores `completion_percentage`, never the accepted quantity, even
 * though a quantity is what the user types. Every screen therefore converts back
 * and forth, and every lost digit lands straight on the money, scaled by
 * `ordered_qty * unit_rate / 100`.
 *
 * That is not theoretical. Rounding the percentage to 2 dp on a 9 szt x 150,00
 * PLN line turned 7 szt (1050,00 PLN) into 1050,03 PLN in production
 * (POCK ELEKTIM/TD33/2026/07/001/POCK/01) — the "a few groszy appeared in the
 * final amount" report. 4 dp is not enough either: a real 92 x 600,00 PLN line
 * loses a grosz. So the percentage always travels at the column's full
 * precision (decimal_places=10), which keeps the error below half a grosz for
 * any line worth less than 1e10 PLN — more than `amount_net` can even store.
 *
 * The backend applies the same rules in `apps/deliveries/pock_math.py` and, as a
 * second line of defence, snaps the reconstructed quantity back onto the 0.01
 * grid — so even a stale tab running old code cannot mint groszy any more.
 */

/** decimal_places of AcceptanceProtocolItem.completion_percentage. */
export const PCT_DECIMALS = 10;
/** max_digits of the same column — the API rejects anything wider. */
export const PCT_MAX_DIGITS = 13;
/** decimal_places of OrderLineItem.quantity — the grid every quantity sits on. */
export const QTY_DECIMALS = 2;

/**
 * Round half away from zero at `dp` decimals.
 *
 * Plain `toFixed` rounds the binary double, not the decimal the user sees, so
 * `(1.005).toFixed(2)` is "1.00". The relative nudge cancels that artefact
 * without disturbing values that are already exact.
 */
export function roundTo(value: number, dp: number): number {
  if (!Number.isFinite(value)) return 0;
  const factor = 10 ** dp;
  const scaled = value * factor;
  const nudged = scaled + Math.sign(scaled) * Math.abs(scaled) * Number.EPSILON;
  return (Math.sign(nudged) * Math.round(Math.abs(nudged))) / factor;
}

const num = (value: string | number | null | undefined, fallback = 0): number => {
  const n = typeof value === "number" ? value : parseFloat(value ?? "");
  return Number.isFinite(n) ? n : fallback;
};

/**
 * The percentage to send for an accepted quantity, at full column precision.
 *
 * `qualityPct` is folded in on purpose — that is the contract the API expects:
 * completion = qty / ordered * quality.
 */
export function completionPctFromQty(
  qty: string | number,
  orderedQty: string | number,
  qualityPct: string | number = 100,
): string {
  const ordered = num(orderedQty);
  if (!ordered) return (0).toFixed(PCT_DECIMALS);
  const pct = (num(qty) / ordered) * num(qualityPct, 100);
  // A percentage over 100 means someone accepted more than was ordered. It is
  // nonsense, but it used to be storable, so keep it storable: give back the
  // decimals that still fit in max_digits rather than a value the API rejects.
  const intDigits = Math.max(1, Math.floor(Math.abs(pct)).toString().length);
  const decimals = Math.max(0, Math.min(PCT_DECIMALS, PCT_MAX_DIGITS - intDigits));
  return roundTo(pct, decimals).toFixed(decimals);
}

/**
 * The accepted quantity behind a stored percentage, snapped onto the 0.01 grid.
 *
 * The snap is what makes the round trip stable: reading a quantity out of a
 * protocol and writing it straight back must not move the amount, which is
 * exactly how re-saving an untouched edit form used to shave off a grosz.
 */
export function acceptedQtyFromPct(
  completionPct: string | number,
  orderedQty: string | number,
  qualityPct: string | number = 100,
): number {
  const quality = num(qualityPct, 100);
  if (!quality) return 0;
  return roundTo((num(completionPct) / quality) * num(orderedQty), QTY_DECIMALS);
}

/** Same quantity, trimmed for display or for seeding a numeric <input>. */
export function qtyInputValue(qty: number): string {
  return String(roundTo(qty, QTY_DECIMALS));
}

/** Net value of a quantity of work: qty x rate, less quality and settlement. */
export function amountFromQty(
  qty: string | number,
  unitRate: string | number,
  qualityPct: string | number = 100,
  settlementPct: string | number = 100,
): number {
  return roundTo(
    (num(qty) * num(unitRate) * num(qualityPct, 100) * num(settlementPct, 100)) / 10000,
    2,
  );
}

/**
 * Did a human actually type this amount, or is the stored value just the
 * formula's own result written down?
 *
 * Approving a POCK request stores `amount_net` on every line, so "stored" is not
 * the same as "overridden". Reading it as an override froze the money: the edit
 * form stopped recomputing the value when the coordinator changed the accepted
 * quantity, leaving a protocol with a new quantity and the old amount.
 */
export function isAmountOverridden(
  storedAmountNet: string | number | null | undefined,
  computedAmount: string | number,
): boolean {
  if (storedAmountNet === null || storedAmountNet === undefined || storedAmountNet === "") {
    return false;
  }
  return roundTo(num(storedAmountNet), 2) !== roundTo(num(computedAmount), 2);
}

/**
 * Net value of a POCK line, derived the same way the backend derives it:
 * snap the quantity first, then multiply.
 */
export function itemAmountNet(
  orderedQty: string | number,
  unitRate: string | number,
  completionPct: string | number,
  qualityPct: string | number = 100,
  settlementPct: string | number = 100,
): number {
  const quality = num(qualityPct, 100);
  return amountFromQty(
    acceptedQtyFromPct(completionPct, orderedQty, quality),
    unitRate,
    quality,
    settlementPct,
  );
}
